import { createHierarchyTree, walk_tree, process_fast_locations } from './helpers.mjs';

const UNSAVED_CHANGES = '_upc';
export let storage;
try {
	storage = window.localStorage;
	storage.getItem('x');
} catch (e) {
	storage = {
		getItem() {
			return null;
		},
		setItem() {},
		removeItem() {},
	};
}

export const PROFILE_KEYS = Object.freeze({
	CACHE_FILTER_KEY: 'cfk',
	USER_ID: 'uid',
	REVISION: 'r',
	HOME_LOCATION: 'hl',
	PRIVACY_NOTICE: 'pvno',
});
export default class ProfileStorage {
	revision = 2;
	profile_hash = null;
	profile_value = null;
	constructor() {
		this.profile_value = JSON.parse(storage.getItem('profile_value')) || {};
		this.profile_hash = storage.getItem('profile_hash');
		this.changesToSaveLater = JSON.parse(storage.getItem(UNSAVED_CHANGES)) || {};
	}

	setKey(key, value) {
		this.profile_value[key] = value;
		this.changesToSaveLater[key] = value;
		storage.setItem(UNSAVED_CHANGES, JSON.stringify(this.changesToSaveLater));
		return this.saveUserProfile().then(() => {
			delete this.changesToSaveLater[key];
			storage.removeItem(UNSAVED_CHANGES);
		});
	}
	getValue(key) {
		return this.profile_value[key];
	}
	getUserProfile() {
		return new window.WebServicePromise({
			url: '/Services/UsersWS.svc/GetUserProfile',
		}).then((profile) => {
			return {
				...profile,
				profile_value: JSON.parse(profile.profile_value) || {},
			};
		});
	}
	mergeValues(value) {
		if (value) {
			const keys = Object.keys(value);
			for (let i = 0; i < keys.length; i++) {
				this.profile_value[keys[i]] = value[keys[i]];
			}
			if (this.changesToSaveLater) {
				const keys = Object.keys(this.changesToSaveLater);
				for (let i = 0; i < keys.length; i++) {
					this.profile_value[keys[i]] = this.changesToSaveLater[keys[i]];
				}
			}
		}
		return this.profile_value;
	}
	saveUserProfile() {
		const deferred = $.Deferred();
		new window.WebServicePromise({
			url: '/Services/UsersWS.svc/SaveUserProfile',
			data: {
				profile_hash: this.profile_hash,
				profile_value: JSON.stringify(this.profile_value),
				revision: this.revision,
			},
		})
			.then((response) => {
				this.profile_value = JSON.parse(response.profile_value);
				this.profile_hash = response.profile_hash;
				storage.setItem('profile_value', JSON.stringify(this.profile_value));
				storage.setItem('profile_hash', this.profile_hash);
				deferred.resolve();
				return this.profile_value;
			})
			.fail(async (e) => {
				if (e.status === 409) {
					const userProfile = await this.getUserProfile();
					let new_profile = userProfile.profile_value;
					this.profile_hash = userProfile.profile_hash;
					this.mergeValues(new_profile);
					await this.saveUserProfile();
				} else {
					if (e.status >= 400 && e.status < 500) {
						window.displayConfirmation(
							window.sw2.intl['common.profile-storage.save-settings-failed'].format(),
							window.sw2.intl['common.profile-storage.error'].format(),
						);
					}
				}
				deferred.resolve();
			});
		return deferred;
	}
	async validateProfile(new_profile_hash) {
		let profile = JSON.parse(storage.getItem('profile_value'));
		if (profile === null) {
			profile = await this.getUserProfile();
			new_profile_hash = profile.profile_hash;
			profile = profile.profile_value;
			this.mergeValues(profile);
		}
		if (profile[PROFILE_KEYS.USER_ID] && profile[PROFILE_KEYS.USER_ID] !== window.sw2.loginService.userid) {
			this.profile_value = {};
			this.profile_hash = null;
			storage.removeItem('profile_value');
			storage.removeItem('profile_hash');
			profile = await this.getUserProfile();
			new_profile_hash = profile.profile_hash;
			profile = profile.profile_value;
			this.mergeValues(profile);
			storage.setItem('profile_value', JSON.stringify(this.profile_value));
			storage.setItem('profile_hash', new_profile_hash);
		} else if (!profile[PROFILE_KEYS.USER_ID]) {
			profile[PROFILE_KEYS.USER_ID] = window.sw2.loginService.userid;
		}
		if (profile[PROFILE_KEYS.REVISION] !== this.revision) {
			const upgraded_profile = await this.upgradeUserProfile(profile, profile[PROFILE_KEYS.REVISION]);
			this.mergeValues(upgraded_profile);
			await this.saveUserProfile(upgraded_profile, new_profile_hash);
		} else {
			if (this.profile_hash !== new_profile_hash) {
				profile = await this.getUserProfile();
				new_profile_hash = profile.profile_hash;
				profile = profile.profile_value;
				this.mergeValues(profile);
			}
			this.profile_hash = new_profile_hash;
		}
		return Promise.resolve();
	}
	async upgradeUserProfile(profile, pre_upgrade_version) {
		const new_profile = Object.assign({}, profile);
		if (!pre_upgrade_version) {
			const FILTER_VALUES_KEY = 'scheduler_filter_values';
			var s = FILTER_VALUES_KEY + '_' + window.sw2.loginService.userid + '_' + window.sw2.loginService.databaseid;
			var item = storage.getItem(s);
			const old_cookie_value = window.sw2.getCookie(s);
			if ((item === 'undefined' || item === null) && old_cookie_value != '') {
				item = old_cookie_value;
				document.cookie = `${s}=; ${window.sw2.intl['common.profile-storage.expires-to'].format()}`;
			}
			if (!window.Office) {
				let filterObj = storage.getItem('filterObject');
				if (filterObj) {
					item = JSON.stringify(JSON.parse(filterObj).values);
					new_profile[PROFILE_KEYS.CACHE_FILTER_KEY] = {};
					new_profile[PROFILE_KEYS.CACHE_FILTER_KEY] = JSON.parse(item);
				}
				filterObj = storage.getItem('filterObject1');
				if (filterObj) {
					item = JSON.stringify(JSON.parse(filterObj).values);
					if (new_profile[PROFILE_KEYS.CACHE_FILTER_KEY]) {
						new_profile[PROFILE_KEYS.CACHE_FILTER_KEY] = {};
					}
					new_profile[PROFILE_KEYS.CACHE_FILTER_KEY + '1'] = JSON.parse(item);
				}
				filterObj = storage.getItem('filterObject2');
				if (filterObj) {
					item = JSON.stringify(JSON.parse(filterObj).values);
					if (!new_profile[PROFILE_KEYS.CACHE_FILTER_KEY]) {
						new_profile[PROFILE_KEYS.CACHE_FILTER_KEY] = {};
					}
					new_profile[PROFILE_KEYS.CACHE_FILTER_KEY + '2'] = JSON.parse(item);
				}
				filterObj = storage.getItem('filterObject3');
				if (filterObj) {
					item = JSON.stringify(JSON.parse(filterObj).values);
					if (!new_profile[PROFILE_KEYS.CACHE_FILTER_KEY]) {
						new_profile[PROFILE_KEYS.CACHE_FILTER_KEY] = {};
					}
					new_profile[PROFILE_KEYS.CACHE_FILTER_KEY + '3'] = JSON.parse(item);
				}
				filterObj = storage.getItem('filterObject4');
				if (filterObj) {
					item = JSON.stringify(JSON.parse(filterObj).values);
					if (!new_profile[PROFILE_KEYS.CACHE_FILTER_KEY]) {
						new_profile[PROFILE_KEYS.CACHE_FILTER_KEY] = {};
					}
					new_profile[PROFILE_KEYS.CACHE_FILTER_KEY + '4'] = JSON.parse(item);
				}
			} else {
				let filterObj = window.Office.context.roamingSettings.get('filterObject');
				if (filterObj) {
					item = JSON.stringify(JSON.parse(filterObj).values);
					new_profile[PROFILE_KEYS.CACHE_FILTER_KEY] = {};
					new_profile[PROFILE_KEYS.CACHE_FILTER_KEY] = JSON.parse(item);
				}
				filterObj = window.Office.context.roamingSettings.get('filterObject1');
				if (filterObj) {
					item = JSON.stringify(JSON.parse(filterObj).values);
					if (new_profile[PROFILE_KEYS.CACHE_FILTER_KEY]) {
						new_profile[PROFILE_KEYS.CACHE_FILTER_KEY] = {};
					}
					new_profile[PROFILE_KEYS.CACHE_FILTER_KEY + '1'] = JSON.parse(item);
				}
				filterObj = window.Office.context.roamingSettings.get('filterObject2');
				if (filterObj) {
					item = JSON.stringify(JSON.parse(filterObj).values);
					if (!new_profile[PROFILE_KEYS.CACHE_FILTER_KEY]) {
						new_profile[PROFILE_KEYS.CACHE_FILTER_KEY] = {};
					}
					new_profile[PROFILE_KEYS.CACHE_FILTER_KEY + '2'] = JSON.parse(item);
				}
				filterObj = window.Office.context.roamingSettings.get('filterObject3');
				if (filterObj) {
					item = JSON.stringify(JSON.parse(filterObj).values);
					if (!new_profile[PROFILE_KEYS.CACHE_FILTER_KEY]) {
						new_profile[PROFILE_KEYS.CACHE_FILTER_KEY] = {};
					}
					new_profile[PROFILE_KEYS.CACHE_FILTER_KEY + '3'] = JSON.parse(item);
				}
				filterObj = window.Office.context.roamingSettings.get('filterObject4');
				if (filterObj) {
					item = JSON.stringify(JSON.parse(filterObj).values);
					if (!new_profile[PROFILE_KEYS.CACHE_FILTER_KEY]) {
						new_profile[PROFILE_KEYS.CACHE_FILTER_KEY] = {};
					}
					new_profile[PROFILE_KEYS.CACHE_FILTER_KEY + '4'] = JSON.parse(item);
				}
			}
		}
		if (pre_upgrade_version === 1) {
			/* SWP-10239 Fix inaccurate group ids in cfk if not all children selected
					Changes already made to prevent this propagating via filters */

			// const allLocations = window.sw2.settingsService._all_locations;
			let allLocations = null;
			const hierarchyTree = async (resourceTypeIds) => {
				if (!allLocations) {
					const allLocationsRaw = await new window.WebServicePromise({
						type: 'GET',
						url: '/webapi/reporting/getdata/locationfieldsettingandrestype',
						headers: {
							auth_token: window.sw2.loginService.logintoken,
							hubstar_context: 'sw2_public',
							'order.orderby': 'id',
							'order.ordertype': 'a',
						},
						data: null,
					});
					allLocations = process_fast_locations(
						(allLocationsRaw.view ?? []).filter((l) => l.t === 2),
						allLocationsRaw,
					);
				}
				/* Not checking hidden locations - add unnecessary complexity and will be remedied further into the app it needed */
				const locations = resourceTypeIds.length
					? allLocations.filter((l) => {
							return l.locationtype === 2 || l._resourceTypes.some((rt) => resourceTypeIds.includes(rt));
						})
					: allLocations;
				return createHierarchyTree(locations);
			};
			const cfks = Object.keys(new_profile).filter((key) => {
				if (key.indexOf(PROFILE_KEYS.CACHE_FILTER_KEY) !== 0) {
					return false;
				} else {
					/* ensure the cfk is valid */
					const formId = key.replace(PROFILE_KEYS.CACHE_FILTER_KEY, '');
					return formId.length === 0 || !isNaN(parseInt(formId, 10));
				}
			});
			for (const key of cfks) {
				const filterObj = new_profile[key];
				const formId = key.replace(PROFILE_KEYS.CACHE_FILTER_KEY, '');
				let new_value = filterObj.locationfilter?.slice() || [];
				if (filterObj && new_value.length) {
					const checked_state = {};
					const resourcetypefilter = formId ? filterObj.resourcetypefilter.map((f) => parseInt(f)) : [];
					const hierarchyTreeResult = await hierarchyTree(resourcetypefilter || []);
					walk_tree(checked_state, hierarchyTreeResult, new_value);
					new_value = new_value.filter((current_value) => {
						const valid = checked_state[current_value] === 'full';
						// remove values so we don't need to check again if they have been added
						// allows is to add any key still left with a value of `full` checked state
						delete checked_state[current_value];
						return valid;
					});
					for (let key in checked_state) {
						if (checked_state[key] === 'full') {
							const try_parse = parseInt(key);
							key = isNaN(try_parse) ? key : try_parse;
							new_value.push(key);
						}
					}
					/* set the value back to the key */
					new_profile[key].locationfilter = new_value;
				}
			}
		}
		new_profile[PROFILE_KEYS.USER_ID] = window.sw2.loginService.userid;
		new_profile[PROFILE_KEYS.REVISION] = this.revision;
		return new_profile;
	}
}
